import { useEffect, useRef, useState } from 'react'

/** True when the user asked for reduced motion (or we can't tell in a
 *  non-browser env, where we should not animate). */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return true
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * Animate a number from 0 up to `target` on mount (ease-out cubic), settling on
 * the exact `target`. Honours `prefers-reduced-motion` (and SSR / no-matchMedia
 * envs) by jumping straight to the final value. Used by the referral stat cards.
 */
export function useCountUp(target: number, durationMs = 1100): number {
  const [value, setValue] = useState<number>(() => (prefersReducedMotion() ? target : 0))
  const frameRef = useRef<number | null>(null)

  useEffect(() => {
    if (prefersReducedMotion()) {
      setValue(target)
      return
    }
    let start: number | null = null
    const tick = (ts: number) => {
      if (start === null) start = ts
      const progress = Math.min(1, (ts - start) / durationMs)
      const eased = 1 - Math.pow(1 - progress, 3)
      setValue(Math.round(target * eased))
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(tick)
      } else {
        setValue(target)
      }
    }
    frameRef.current = requestAnimationFrame(tick)
    return () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
    }
  }, [target, durationMs])

  return value
}
